import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { InsightBar } from '../../components/charts/InsightBar.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { countBy, topBuckets } from '../../utils/insightStats.js';

import { CandidateCreateModal } from './CandidateCreateModal.jsx';

export function CandidatesPage() {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['hr', 'candidates'],
    queryFn: () => hrApi.listCandidates().then((r) => r.items || []),
  });

  const stats = useMemo(() => {
    const rows = data || [];
    const counts = countBy(rows, (r) => r.source);
    return {
      total: rows.length,
      buckets: topBuckets(counts),
      distinctSources: Object.keys(counts).filter((key) => key !== '-').length,
      withPhone: rows.filter((r) => r.phone).length,
    };
  }, [data]);

  const columns = [
    { key: 'name',   label: t('col.name'), render: (r) => `${r.firstName} ${r.lastName}` },
    { key: 'email',  label: t('col.email') },
    { key: 'phone',  label: t('rec.candidates.col.phone') },
    { key: 'source', label: t('rec.candidates.col.source') },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('rec.candidates.title')}</h1>
          <p className="muted">{t('rec.candidates.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            {t('rec.candidates.action.new')}
          </Button>
        </div>
      </header>
      {stats.total > 0 && (
        <InsightBar
          title={t('insight.bySource')}
          labels={stats.buckets.map(([source]) => source)}
          values={stats.buckets.map(([, count]) => count)}
          metrics={[
            { label: t('insight.count'), value: stats.total },
            { label: t('rec.candidates.insight.sources'), value: stats.distinctSources },
            { label: t('rec.candidates.insight.withPhone'), value: stats.withPhone },
          ]}
        />
      )}
      {error && <div className="alert danger">{error.message}</div>}
      <Table columns={columns} rows={data} isLoading={isLoading} emptyMessage={t('rec.candidates.empty')} />
      <CandidateCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </section>
  );
}
